/**
 * config/products.ts
 * ──────────────────────────────────────────────────────────────────────────
 * The client-side open-redirect guard for centralized auth.
 *
 * After a user authenticates on the platform, we redirect them back into the
 * product they came from using a `return_to` URL supplied by the website CTA.
 * That URL is attacker-influençable, so it MUST be validated before we ever
 * navigate to it. The list of legitimate product origins is NOT hardcoded here
 * (the backend product registry is the single source of truth); we fetch it
 * once from GET /v1/products/redirect-targets and cache it for the session.
 *
 * resolveReturnTarget() is the only place that decides where a post-auth
 * redirect may go.
 */
import axiosInstance from '../api-endpoints/axiosInstance';
import { Platform_Redirect_Targets_Url } from '../api-endpoints/api-endpoints';

/** slug → allow-listed FE origin, e.g. { catalog: 'https://catalog.staging.softknack.com' }. */
type RedirectTargets = Record<string, string>;

// Memoized fetch — one request per session. On failure we clear the promise so
// a later call can retry rather than caching the error permanently.
let _targetsInflight: Promise<RedirectTargets> | null = null;

async function getTargets(): Promise<RedirectTargets> {
  if (!_targetsInflight) {
    _targetsInflight = axiosInstance
      .get(Platform_Redirect_Targets_Url)
      .then((r) => (r.data?.data?.targets ?? {}) as RedirectTargets)
      .catch((err) => {
        _targetsInflight = null;
        throw err;
      });
  }
  return _targetsInflight;
}

/**
 * Decide the safe URL to redirect to after auth, for a given originating
 * product and requested `return_to`. Returns:
 *   • null               → unknown / unconfigured product: do NOT redirect
 *                          (stay in the platform shell).
 *   • product origin root → product is known but `return_to` is missing,
 *                          unparseable, or points at a different origin
 *                          (open-redirect attempt) — fall back to a safe default.
 *   • the full return_to → only when its origin EXACTLY matches the product's
 *                          allow-listed origin.
 */
export async function resolveReturnTarget(
  product: string | null,
  returnTo: string | null,
): Promise<string | null> {
  if (!product) return null;

  let targets: RedirectTargets;
  try {
    targets = await getTargets();
  } catch {
    // Can't load the allow-list → never redirect to an unverified origin.
    return null;
  }

  const allowedOrigin = targets[product];
  if (!allowedOrigin) return null; // product not in the allow-list

  if (!returnTo) return allowedOrigin; // default to the product root

  let url: URL;
  try {
    url = new URL(returnTo);
  } catch {
    return allowedOrigin; // unparseable → safe default
  }

  // Exact-origin match binds the product to its own origin and blocks
  // redirecting one product's CTA into a different (or hostile) origin.
  if (url.origin !== allowedOrigin) return allowedOrigin;

  return url.href;
}
